if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        this.isLanguageSelector = this.elements.input?.name === 'locale_code';

        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));

        if (this.elements.button) {
          this.elements.button.addEventListener('click', this.openSelector.bind(this));
        }

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }

        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }

        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
        this.syncCurrencyDisplayFromStorage();
      }

      hidePanel() {
        if (this.elements.button) {
          this.elements.button.setAttribute('aria-expanded', 'false');
        }
        this.elements.panel?.setAttribute('hidden', true);

        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }

        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );

        if (!focusableItems.length) return;

        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
          default:
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (!this.elements.button || this.elements.button.getAttribute('aria-expanded') === 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (!this.elements.button || this.elements.button.getAttribute('aria-expanded') === 'true') return;
            this.openSelector();
            break;
          default:
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();

        const form = this.querySelector('form');
        const selectedLink = event.currentTarget;
        const targetValue = (selectedLink.dataset.value || '').trim();
        const currencyCode = this.normalizeCurrencyCode(selectedLink.dataset.currencyCode);
        const currencySymbol = selectedLink.dataset.currencySymbol || this.getSymbolForCurrency(currencyCode);

        if (!form || !targetValue) {
          return;
        }

        if (this.elements.input) {
          this.elements.input.value = targetValue;
        }

        let returnToInput = form.querySelector('input[name="return_to"]');
        if (!returnToInput) {
          returnToInput = document.createElement('input');
          returnToInput.type = 'hidden';
          returnToInput.name = 'return_to';
          form.appendChild(returnToInput);
        }

        const returnUrl = new URL(window.location.href);
        const returnPath = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
        returnToInput.value = returnPath;

        const continueAfterUiReset = async () => {
          await this.prepareForNavigation();

          if (this.isLanguageSelector) {
            form.submit();
            return;
          }

          if (currencyCode) {
            this.updateCurrencyButtonText(currencyCode);
            this.highlightCurrencyLink(currencyCode);
            this.persistCurrencyPreference(currencyCode, currencySymbol);
          }

          this.persistCountryPreference(targetValue);

          if (currencyCode) {
            returnUrl.searchParams.set('currency', currencyCode);
          }
          returnToInput.value = returnPath;

          this.submitLocalizationForm(form, targetValue, returnPath).then((submitted) => {
            if (!submitted) {
              form.submit();
            }
          });
        };

        continueAfterUiReset();
      }

      persistCurrencyPreference(currencyCode, currencySymbol) {
        const normalizedCode = this.normalizeCurrencyCode(currencyCode);
        if (!normalizedCode) return;

        const symbol = currencySymbol || this.getSymbolForCurrency(normalizedCode);

        try {
          localStorage.setItem('currency', normalizedCode);
          localStorage.setItem('selected_currency', normalizedCode);
          localStorage.setItem('cart_currency', normalizedCode);
          localStorage.setItem('currency_code', normalizedCode);
          localStorage.setItem('shopify_currency', normalizedCode);
          localStorage.setItem('sf_currency_code', normalizedCode);
          localStorage.setItem('sf_currency_symbol', symbol);

          sessionStorage.setItem('currency', normalizedCode);
          sessionStorage.setItem('selected_currency', normalizedCode);
        } catch (error) {
          console.warn(error);
        }

        const cookieAge = 60 * 60 * 24 * 365;
        document.cookie = `currency=${normalizedCode}; path=/; max-age=${cookieAge}`;
        document.cookie = `selected_currency=${normalizedCode}; path=/; max-age=${cookieAge}`;
        document.cookie = `cart_currency=${normalizedCode}; path=/; max-age=${cookieAge}`;
        document.cookie = `currency_code=${normalizedCode}; path=/; max-age=${cookieAge}`;
        document.cookie = `shopify_currency=${normalizedCode}; path=/; max-age=${cookieAge}`;
        document.documentElement.setAttribute('data-selected-currency', normalizedCode);

        window.dispatchEvent(
          new CustomEvent('currency:change', {
            detail: { currencyCode: normalizedCode, currencySymbol: symbol },
          })
        );
      }

      persistCountryPreference(countryCode) {
        const normalizedCountry = `${countryCode || ''}`.trim().toUpperCase();
        if (!normalizedCountry) return;

        const cookieAge = 60 * 60 * 24 * 365;
        document.cookie = `localization=${normalizedCountry}; path=/; max-age=${cookieAge}`;
        document.cookie = `_shopify_country=${normalizedCountry}; path=/; max-age=${cookieAge}`;
      }

      async submitLocalizationForm(form, targetCountryCode, returnUrl) {
        if (!form || !targetCountryCode) return false;

        try {
          const formData = new FormData(form);
          formData.set('country_code', targetCountryCode);
          formData.set('return_to', returnUrl);

          const actionUrl = form.getAttribute('action') || `${window.Shopify?.routes?.root || '/'}localization`;
          const response = await fetch(actionUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });

          if (!response.ok) {
            throw new Error(`Unable to update localization: ${response.status}`);
          }

          window.location.assign(returnUrl);
          return true;
        } catch (error) {
          console.warn(error);
          return false;
        }
      }

      syncCurrencyDisplayFromStorage() {
        const currencyItems = this.querySelectorAll('a[data-currency-code]');
        if (!currencyItems.length) return;

        const storedCurrencyCode = this.normalizeCurrencyCode(
          this.readCookie('selected_currency') ||
            this.readCookie('currency') ||
            this.safeStorageGet('selected_currency') ||
            this.safeStorageGet('currency') ||
            this.safeStorageGet('sf_currency_code')
        );

        if (storedCurrencyCode) {
          this.updateCurrencyButtonText(storedCurrencyCode);
          this.highlightCurrencyLink(storedCurrencyCode);
          this.persistCurrencyPreference(storedCurrencyCode);
          return;
        }

        const renderedCurrencyCode = this.normalizeCurrencyCode(
          this.elements.button?.querySelector('[data-current-currency-code]')?.dataset.currentCurrencyCode ||
            this.elements.button?.querySelector('[data-current-currency-code]')?.textContent
        );

        if (renderedCurrencyCode) {
          this.updateCurrencyButtonText(renderedCurrencyCode);
          this.highlightCurrencyLink(renderedCurrencyCode);
          return;
        }

        const currentLink = this.querySelector('a[data-currency-code][aria-current="true"]');
        if (!currentLink) return;

        const currentCode = this.normalizeCurrencyCode(currentLink.dataset.currencyCode);
        if (!currentCode) return;

        this.updateCurrencyButtonText(currentCode);
        this.highlightCurrencyLink(currentCode);
      }

      updateCurrencyButtonText(currencyCode, currencySymbol) {
        if (!this.elements.button) return;
        const buttonText = this.elements.button.querySelector('span');
        if (!buttonText) return;

        buttonText.textContent = `${currencyCode}`;
      }

      highlightCurrencyLink(currencyCode) {
        this.querySelectorAll('a[data-currency-code]').forEach((link) => {
          const isCurrent = link.dataset.currencyCode === currencyCode;
          if (isCurrent) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }

          const check = link.querySelector('span');
          if (check) {
            check.classList.toggle('visibility-hidden', !isCurrent);
          }
        });
      }

      getSymbolForCurrency(currencyCode) {
        switch (currencyCode) {
          case 'USD':
            return '$';
          case 'EUR':
            return '\u20AC';
          case 'GBP':
            return '\u00A3';
          case 'AED':
            return 'AED';
          case 'KRW':
            return '\u20A9';
          default:
            return '';
        }
      }

      normalizeCurrencyCode(currencyCode) {
        if (!currencyCode) return '';
        return `${currencyCode}`.trim().toUpperCase();
      }

      safeStorageGet(key) {
        try {
          return localStorage.getItem(key) || sessionStorage.getItem(key) || '';
        } catch (error) {
          return '';
        }
      }

      readCookie(name) {
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : '';
      }

      buildCurrencyReturnUrl(currencyCode, countryCode = '') {
        const currentUrl = new URL(window.location.href);
        if (currencyCode) {
          currentUrl.searchParams.set('currency', currencyCode);
        }
        if (countryCode) {
          currentUrl.searchParams.set('country', countryCode);
        }
        return `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
      }

      async prepareForNavigation() {
        this.hidePanel();

        document.querySelectorAll('header-drawer, menu-drawer').forEach((drawer) => {
          if (typeof drawer.forceCloseMenuDrawer === 'function') {
            drawer.forceCloseMenuDrawer();
          }
        });

        await new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(resolve);
          });
        });
      }

      openSelector() {
        this.elements.button?.focus();
        this.elements.panel?.toggleAttribute('hidden');

        if (this.elements.button) {
          this.elements.button.setAttribute(
            'aria-expanded',
            (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
          );
        }

        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }

        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }

        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }

        document.querySelector('.menu-drawer')?.classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        if (!this.elements.search) return;

        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = 0;

        if (this.elements.resetButton) {
          this.elements.resetButton.classList.toggle('hidden', !searchValue);
        }

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country')?.textContent || '');
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries += 1;
          } else {
            item.parentElement.classList.add('hidden');
          }
        });

        if (this.elements.liveRegion && window.accessibilityStrings?.countrySelectorSearchCount) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        const selector = this.querySelector('.country-selector');
        if (selector) selector.scrollTop = 0;

        const selectorList = this.querySelector('.country-selector__list');
        if (selectorList) selectorList.scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        if (!this.elements.search) return;
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        if (this.elements.searchIcon) {
          this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
        }
      }

      onSearchBlur() {
        if (!this.elements.search?.value && this.elements.searchIcon) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
